import React, { useState, useEffect } from "react";
import { Trash2, Send, Loader2, CheckCircle, XCircle, GitBranch, Eye, Mail, Plus } from "lucide-react";
import { apiClient } from "../../App";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Textarea } from "../ui/textarea";
import { Badge } from "../ui/badge";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "../ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "../ui/alert-dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../ui/select";
import { statusLabels } from "../../lib/contractUtils";

export function SignatureDialog({ open, onOpenChange, signatureData, setSignatureData, onSubmit }) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Send for Signature via DocuSign</DialogTitle>
          <DialogDescription>Enter the supplier&apos;s details. A DocuSign envelope will be created. After you sign, the contract will be automatically sent to the supplier for counter-signature.</DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-4">
          <div className="space-y-2">
            <Label>Signer Name</Label>
            <Input value={signatureData.name} onChange={(e) => setSignatureData({ ...signatureData, name: e.target.value })} placeholder="John Doe" data-testid="signer-name-input" />
          </div>
          <div className="space-y-2">
            <Label>Signer Email</Label>
            <Input type="email" value={signatureData.email} onChange={(e) => setSignatureData({ ...signatureData, email: e.target.value })} placeholder="john@company.com" data-testid="signer-email-input" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} className="rounded-sm">Cancel</Button>
          <Button onClick={onSubmit} className="rounded-sm" data-testid="send-signature-btn"><Send size={16} className="mr-2" /> Send Request</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function WorkflowDialog({
  open, onOpenChange, users, user, policySuggestions,
  selectedReviewers, setSelectedReviewers,
  selectedApprovers, setSelectedApprovers,
  externalReviewers, setExternalReviewers,
  workflowDueDate, setWorkflowDueDate,
  onSubmit, toast, contractCreatorId,
}) {
  const [extName, setExtName] = useState("");
  const [extEmail, setExtEmail] = useState("");

  const addExternalReviewer = () => {
    if (!extName.trim() || !extEmail.trim()) { toast.error("Name and email are required"); return; }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(extEmail)) { toast.error("Invalid email address"); return; }
    if (externalReviewers.some(r => r.email === extEmail)) { toast.error("This email is already added"); return; }
    setExternalReviewers([...externalReviewers, { name: extName.trim(), email: extEmail.trim() }]);
    setExtName(""); setExtEmail("");
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Submit for Review & Approval</DialogTitle>
          <DialogDescription>Select reviewers for quality check and approvers for final authorization. A user cannot serve as both reviewer and approver. The contract creator cannot be an approver (segregation of duty).</DialogDescription>
        </DialogHeader>
        <div className="space-y-5 py-4">
          {policySuggestions?.matching_policies?.length > 0 && (
            <div className="p-3 bg-cyan-50 border border-cyan-200 rounded-sm text-sm">
              <p className="font-medium text-cyan-800 mb-1">Policy-based suggestions applied:</p>
              {policySuggestions.matching_policies.map((p, i) => (
                <p key={i} className="text-cyan-700 text-xs">- {p.name}: {p.description || "Conditions matched"}</p>
              ))}
            </div>
          )}

          <div className="space-y-2">
            <Label>Due Date (optional)</Label>
            <Input type="date" value={workflowDueDate} onChange={(e) => setWorkflowDueDate(e.target.value)} className="rounded-sm" data-testid="workflow-due-date" />
          </div>

          <UserSelector
            label={<><Eye size={16} className="text-amber-600" /> Reviewers (Quality Check)</>}
            selected={selectedReviewers}
            setSelected={setSelectedReviewers}
            otherSelected={selectedApprovers}
            users={users}
            currentUserId={user?.id}
            excludeUserIds={[]}
            color="amber"
            selectTestId="reviewer-select"
            itemTestIdPrefix="selected-reviewer"
            toast={toast}
          />

          {/* External Reviewers Section */}
          <div className="space-y-2">
            <Label className="flex items-center gap-2"><Mail size={16} className="text-violet-600" /> External Reviewers (via Email)</Label>
            <p className="text-xs text-slate-500">Add reviewers who are not in the system. They will receive a review request via email. You can record their feedback once received.</p>
            <div className="flex gap-2">
              <Input placeholder="Name" value={extName} onChange={e => setExtName(e.target.value)} className="rounded-sm flex-1" data-testid="ext-reviewer-name" />
              <Input placeholder="Email" type="email" value={extEmail} onChange={e => setExtEmail(e.target.value)} className="rounded-sm flex-1" data-testid="ext-reviewer-email" />
              <Button type="button" variant="outline" size="sm" onClick={addExternalReviewer} className="rounded-sm whitespace-nowrap" data-testid="add-ext-reviewer-btn">
                <Plus size={14} className="mr-1" /> Add
              </Button>
            </div>
            {externalReviewers.length > 0 && (
              <div className="space-y-1.5">
                {externalReviewers.map((er, idx) => (
                  <div key={idx} className="flex items-center justify-between p-2 border rounded-sm bg-violet-50/50" data-testid={`ext-reviewer-${idx}`}>
                    <div className="flex items-center gap-2">
                      <span className="w-5 h-5 rounded-full bg-violet-100 text-violet-700 flex items-center justify-center text-[10px] font-bold"><Mail size={10} /></span>
                      <span className="text-sm font-medium">{er.name}</span>
                      <span className="text-xs text-slate-400">{er.email}</span>
                      <Badge className="rounded-sm text-[9px] bg-violet-100 text-violet-600">external</Badge>
                    </div>
                    <Button variant="ghost" size="sm" className="h-6 text-xs text-red-500" onClick={() => setExternalReviewers(externalReviewers.filter((_, i) => i !== idx))}>Remove</Button>
                  </div>
                ))}
              </div>
            )}
          </div>

          <UserSelector
            label={<><CheckCircle size={16} className="text-blue-600" /> Approvers (Final Authorization)</>}
            selected={selectedApprovers}
            setSelected={setSelectedApprovers}
            otherSelected={selectedReviewers}
            users={users}
            currentUserId={user?.id}
            excludeUserIds={contractCreatorId ? [contractCreatorId] : []}
            color="blue"
            selectTestId="approver-select"
            itemTestIdPrefix="selected-approver"
            toast={toast}
          />
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} className="rounded-sm">Cancel</Button>
          <Button onClick={onSubmit} className="rounded-sm" disabled={selectedApprovers.length === 0} data-testid="create-workflow-btn">
            <GitBranch size={16} className="mr-2" /> Submit for Review
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function UserSelector({ label, selected, setSelected, otherSelected, users, currentUserId, excludeUserIds = [], color, selectTestId, itemTestIdPrefix, toast }) {
  return (
    <div className="space-y-2">
      <Label className="flex items-center gap-2">{label}</Label>
      <Select onValueChange={(value) => {
        if (!selected.includes(value) && !otherSelected.includes(value)) {
          setSelected([...selected, value]);
        } else if (otherSelected.includes(value)) {
          toast.error("This user is already selected in the other role. Segregation of duty applies.");
        }
      }}>
        <SelectTrigger data-testid={selectTestId}><SelectValue placeholder="Add a user" /></SelectTrigger>
        <SelectContent>
          {users.filter((u) => !selected.includes(u.id) && u.id !== currentUserId && !excludeUserIds.includes(u.id)).map((u) => (
            <SelectItem key={u.id} value={u.id}>{u.name} ({u.role})</SelectItem>
          ))}
        </SelectContent>
      </Select>
      {selected.length > 0 && (
        <div className="space-y-1.5">
          {selected.map((uid, idx) => {
            const u = users.find((x) => x.id === uid);
            return (
              <div key={uid} className={`flex items-center justify-between p-2 border rounded-sm bg-${color}-50/50`} data-testid={`${itemTestIdPrefix}-${idx}`}>
                <div className="flex items-center gap-2">
                  <span className={`w-5 h-5 rounded-full bg-${color}-100 text-${color}-700 flex items-center justify-center text-[10px] font-bold`}>{idx + 1}</span>
                  <span className="text-sm font-medium">{u?.name}</span>
                  <span className="text-xs text-slate-400">{u?.role}</span>
                </div>
                <Button variant="ghost" size="sm" className="h-6 text-xs text-red-500" onClick={() => setSelected(selected.filter((r) => r !== uid))}>Remove</Button>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

export function ActionDialog({ open, onOpenChange, actionType, actionComment, setActionComment, onSubmit }) {
  const titles = { "review-approve": "Approve Review", "send-back": "Send Back for Revision", approve: "Final Approval", reject: "Reject Contract" };
  const descriptions = { "send-back": "Provide a reason so the author can make corrections.", reject: "Please explain why this contract is being rejected." };
  const colors = { reject: "bg-red-600 hover:bg-red-700", "send-back": "bg-orange-600 hover:bg-orange-700" };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{titles[actionType] || ""}</DialogTitle>
          <DialogDescription>{descriptions[actionType] || "Add an optional comment."}</DialogDescription>
        </DialogHeader>
        <div className="py-4">
          <Label className="mb-2 block">
            Comment {(actionType === "send-back" || actionType === "reject") && <span className="text-red-500">*</span>}
          </Label>
          <Textarea
            value={actionComment} onChange={(e) => setActionComment(e.target.value)}
            placeholder={actionType === "send-back" ? "Please revise section 3.2..." : actionType === "reject" ? "Reason for rejection..." : "Looks good."}
            rows={3} className="rounded-sm" data-testid="action-comment-input"
          />
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} className="rounded-sm">Cancel</Button>
          <Button onClick={onSubmit} className={`rounded-sm ${colors[actionType] || "bg-emerald-600 hover:bg-emerald-700"}`}
            disabled={(actionType === "send-back" || actionType === "reject") && !actionComment.trim()} data-testid="confirm-action-btn">
            Confirm
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function AddendumDialog({ open, onOpenChange, form, setForm, onSubmit, saving, contract }) {
  const [addendumTemplates, setAddendumTemplates] = useState([]);
  const [loadingTemplates, setLoadingTemplates] = useState(false);

  useEffect(() => {
    if (open) {
      setLoadingTemplates(true);
      apiClient.get("/templates?template_type=addendum")
        .then((res) => setAddendumTemplates(res.data))
        .catch(() => {})
        .finally(() => setLoadingTemplates(false));
    }
  }, [open]);

  // Build contract metadata string for auto-populating Parties section
  const buildPartiesContent = () => {
    const party = contract?.contracting_party || {};
    const cf = contract?.custom_fields || {};
    const lines = ["The Company:", "[ Insert company details ]\n"];
    lines.push("The Service Provider / Supplier:");
    if (party.legal_name) lines.push(party.legal_name);
    if (party.registered_address) lines.push(party.registered_address);
    if (party.contact_email) lines.push(`Email: ${party.contact_email}`);
    if (party.contact_phone) lines.push(`Phone: ${party.contact_phone}`);
    if (cf.supplier_unique_id) lines.push(`Supplier ID: ${cf.supplier_unique_id}`);
    return lines.join("\n");
  };

  const buildAgreementRefContent = () => {
    const cf = contract?.custom_fields || {};
    return `The agreement between the Parties${cf.contract_unique_id ? ` (${cf.contract_unique_id})` : ""} titled "${contract?.title || ""}" dated ${contract?.effective_date || "[ insert date ]"} relating to ${contract?.contract_type || "[ insert description ]"}.`;
  };

  const handleTemplateSelect = (templateId) => {
    if (!templateId || templateId === "none") {
      setForm({ ...form, template_id: null, sections: null });
      return;
    }
    const template = addendumTemplates.find((t) => t.id === templateId);
    if (template) {
      const sections = (template.sections || []).map((s) => {
        const sec = { ...s, id: s.id || `section-${Date.now()}-${Math.random().toString(36).substr(2, 5)}` };
        // Auto-populate from contract metadata
        if (s.type === "parties" && contract) sec.content = buildPartiesContent();
        if (s.type === "agreement_reference" && contract) sec.content = buildAgreementRefContent();
        return sec;
      });
      setForm({
        ...form,
        template_id: templateId,
        sections,
        description: form.description || template.description || "",
      });
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Create Addendum</DialogTitle>
          <DialogDescription>Add an amendment, extension, or variation to this contract</DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-4">
          {/* Contract Reference Banner */}
          {contract && (
            <div className="bg-slate-50 dark:bg-slate-800/50 border rounded-sm p-3 text-sm space-y-1" data-testid="addendum-contract-ref">
              <div className="flex items-center gap-2 text-slate-500 text-xs font-mono uppercase">Original Contract</div>
              <div className="font-medium">{contract.title}</div>
              <div className="text-slate-500 text-xs">
                {contract.custom_fields?.contract_unique_id && <span className="mr-3">{contract.custom_fields.contract_unique_id}</span>}
                {contract.contracting_party?.legal_name && <span>{contract.contracting_party.legal_name}</span>}
              </div>
            </div>
          )}

          {/* Template Selection */}
          <div className="space-y-2">
            <Label>Addendum Template</Label>
            <Select value={form.template_id || "none"} onValueChange={handleTemplateSelect}>
              <SelectTrigger className="rounded-sm" data-testid="addendum-template-select">
                <SelectValue placeholder={loadingTemplates ? "Loading templates..." : "Select a template (optional)"} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="none">No template (blank)</SelectItem>
                {addendumTemplates.map((t) => (
                  <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Addendum Type</Label>
              <Select value={form.addendum_type} onValueChange={(v) => setForm({ ...form, addendum_type: v })}>
                <SelectTrigger className="rounded-sm" data-testid="addendum-type-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="amendment">Amendment</SelectItem>
                  <SelectItem value="extension">Extension</SelectItem>
                  <SelectItem value="variation">Variation</SelectItem>
                  <SelectItem value="supplement">Supplement</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Effective Date</Label>
              <Input type="date" value={form.effective_date} onChange={(e) => setForm({ ...form, effective_date: e.target.value })} className="rounded-sm" data-testid="addendum-effective-date" />
            </div>
          </div>
          <div className="space-y-2">
            <Label>Title *</Label>
            <Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="e.g., Extension to December 2027" className="rounded-sm" data-testid="addendum-title-input" />
          </div>
          <div className="space-y-2">
            <Label>Description *</Label>
            <Textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder="Describe the purpose and scope of this addendum..." rows={3} className="rounded-sm" data-testid="addendum-description-input" />
          </div>
          <div className="space-y-2">
            <Label>Summary of Changes</Label>
            <Textarea value={form.changes_summary} onChange={(e) => setForm({ ...form, changes_summary: e.target.value })} placeholder="Briefly summarise the key changes being made..." rows={2} className="rounded-sm" data-testid="addendum-changes-input" />
          </div>

          {/* Template Sections */}
          {form.sections && form.sections.length > 0 && (
            <div className="space-y-3 border-t pt-4">
              <Label className="text-xs font-mono uppercase tracking-wider text-slate-500">Template Sections</Label>
              {form.sections.map((section, idx) => (
                <div key={section.id || idx} className={`border rounded-sm p-3 space-y-2 ${!section.is_editable ? "bg-amber-50/30 dark:bg-amber-950/10 border-amber-200" : ""}`}>
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium">{section.name}</span>
                    <Badge variant={section.is_editable ? "outline" : "secondary"} className="text-xs">
                      {section.is_editable ? "Editable" : "Locked"}
                    </Badge>
                  </div>
                  {section.is_editable || section.allow_override ? (
                    <Textarea
                      value={section.content}
                      onChange={(e) => {
                        const updated = [...form.sections];
                        updated[idx] = { ...updated[idx], content: e.target.value };
                        setForm({ ...form, sections: updated });
                      }}
                      rows={4}
                      className="rounded-sm text-sm"
                      data-testid={`addendum-section-${idx}`}
                    />
                  ) : (
                    <p className="text-sm text-slate-500 whitespace-pre-wrap">{section.content}</p>
                  )}
                </div>
              ))}
            </div>
          )}

          {(form.addendum_type === "extension" || form.addendum_type === "variation") && (
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>New Expiration Date</Label>
                <Input type="date" value={form.new_expiration_date} onChange={(e) => setForm({ ...form, new_expiration_date: e.target.value })} className="rounded-sm" data-testid="addendum-new-expiry" />
              </div>
              <div className="space-y-2">
                <Label>New Contract Value</Label>
                <Input type="number" value={form.new_value} onChange={(e) => setForm({ ...form, new_value: e.target.value })} placeholder="e.g., 100000" className="rounded-sm" data-testid="addendum-new-value" />
              </div>
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} className="rounded-sm">Cancel</Button>
          <Button onClick={onSubmit} disabled={saving} className="rounded-sm" data-testid="save-addendum-btn">
            {saving && <Loader2 size={16} className="animate-spin mr-2" />} Create Addendum
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function DeleteContractDialog({ open, onOpenChange, contract, onDelete }) {
  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle className="flex items-center gap-2 text-red-600"><Trash2 size={20} /> Delete Contract</AlertDialogTitle>
          <AlertDialogDescription className="space-y-3" asChild>
            <div>
              <p>You are about to delete the following contract:</p>
              <div className="bg-slate-50 dark:bg-slate-800 border rounded-sm p-3 my-2">
                <p className="font-medium text-slate-900 dark:text-white">{contract?.title}</p>
                <p className="text-xs text-slate-500 mt-1">
                  {contract?.contracting_party?.legal_name || "No supplier"} &middot; {statusLabels[contract?.status] || contract?.status}
                </p>
              </div>
              <div className="bg-red-50 dark:bg-red-950/20 border border-red-200 dark:border-red-800 rounded-sm p-3 text-sm text-red-700 dark:text-red-400">
                <p className="font-semibold mb-1">This action cannot be undone.</p>
                <p>The contract and all associated metadata will be permanently removed from the register. If you want to retain the record, consider using <strong>Archive</strong> instead.</p>
              </div>
            </div>
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel className="rounded-sm" data-testid="cancel-delete-dialog-btn">Cancel</AlertDialogCancel>
          <AlertDialogAction onClick={onDelete} className="rounded-sm bg-red-600 hover:bg-red-700" data-testid="confirm-delete-dialog-btn">Delete Permanently</AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
